import * as React from "react"
import { Center, Spinner, useTheme } from "@chakra-ui/react"
import { useDeepCompareMemo } from "use-deep-compare"
import { GeolocateControl, NavigationControl } from "react-map-gl"
import Mapbox, { MapViewStateChangeEvent } from "../mapbox/Mapbox"
import { BikeState } from "../lib/types/bike"
import { ZoneEntity } from "../lib/types/zone"
import { getZonesGeojson } from "../lib/helpers/map"
import { MapBikeEntity, useGetMapBikesQuery } from "../lib/hooks/queries/useGetMapBikesQuery"
import { useMapBBox } from "./providers/MapBBoxProvider"
import { BikesMapDropzonesLayer } from "./BikesMapDropzonesLayer"
import { HandleSearchUpdateArgs } from "./BikesMapContainer"
import GeoJSONLayer from "../mapbox/GeoJSONLayer"
import BikesMapMapboxSupercluster from "./BikesMapMapboxSupercluster"
import { useFillOpacity } from "../lib/hooks/useDropZoneStyle"

export interface MapProps {
  selectedZone: ZoneEntity | undefined
  showServiceZones: boolean
  serviceZones: ZoneEntity[] | undefined
  showDropzones: boolean
  dropzones: ZoneEntity[] | undefined
  showMaintenanceZones: boolean
  maintenanceZones: ZoneEntity[] | undefined
  search: string
  bikeStates: BikeState[]
  bikeModels: string[]
  bikeTags: string[]
  handleSearchUpdate: (args: HandleSearchUpdateArgs) => void
  activeBike: MapBikeEntity | null
}

const MAP_ID = "bikesMapbox"

export function BikesMapMapbox({
  selectedZone,
  showServiceZones,
  serviceZones,
  showDropzones,
  dropzones,
  showMaintenanceZones,
  maintenanceZones,
  search,
  bikeStates,
  bikeModels,
  bikeTags,
  handleSearchUpdate,
  activeBike,
}: Readonly<MapProps>) {
  const theme = useTheme()
  const { currentBBox, setBBoxFromMapbox } = useMapBBox()

  const { bikes, bikesLoading } = useGetMapBikesQuery({
    bbox: currentBBox,
    search,
    bikeStates,
    bikeModels,
    bikeTags,
  })

  const memoBikes = useDeepCompareMemo(() => bikes?.filter((b) => !!b.longitude && !!b.latitude), [bikes])

  React.useEffect(
    () => handleSearchUpdate({ bikes: memoBikes, bikesLoading }),
    [handleSearchUpdate, memoBikes, bikesLoading],
  )

  const handleMapMove = (event: MapViewStateChangeEvent) => {
    if (event.fitboundUpdate) return
    const map = event.target
    const bounds = map.getBounds()
    setBBoxFromMapbox(bounds)
  }

  const polygonsFillOpacity = useFillOpacity()

  return (
    <Mapbox
      id={MAP_ID}
      style={{
        height: "100%",
        width: "100%",
      }}
      fitBounds={currentBBox}
      fitBoundsOptions={{ linear: false }}
      onMoveEnd={handleMapMove}
    >
      {/* CONTROLS */}
      <GeolocateControl />
      <NavigationControl />

      {/* ---------- ZONES ----------- */}
      {!!showDropzones && !!dropzones ? (
        <>
          {dropzones.map((dropzone) => (
            <BikesMapDropzonesLayer key={dropzone.uuid} dropzone={dropzone} selectedZone={selectedZone} />
          ))}
        </>
      ) : undefined}

      {!!showServiceZones && !!serviceZones ? (
        <GeoJSONLayer
          id="assets_map_service"
          data={getZonesGeojson(serviceZones) as GeoJSON.FeatureCollection}
          fillPaint={{
            fillColor: theme.colors.purple["500"],
            fillOpacity: polygonsFillOpacity,
          }}
          linePaint={{
            lineColor: theme.colors.purple["800"],
          }}
        />
      ) : undefined}

      {!!showMaintenanceZones && !!maintenanceZones ? (
        <GeoJSONLayer
          id="assets_map_maintenance"
          data={getZonesGeojson(maintenanceZones) as GeoJSON.FeatureCollection}
          fillPaint={{
            fillColor: theme.colors.gray["500"],
            fillOpacity: polygonsFillOpacity,
          }}
          linePaint={{
            lineColor: theme.colors.gray["800"],
          }}
        />
      ) : undefined}

      {/* ---------- BIKES ----------- */}
      {bikesLoading ? (
        <Center pos="absolute" w="100%" bottom={8}>
          <Center p={2} bg="white" borderRadius="md" h={50} w={50}>
            <Spinner size="sm" />
          </Center>
        </Center>
      ) : undefined}

      <BikesMapMapboxSupercluster bikes={memoBikes} activeBike={activeBike} zoomOnClick />
    </Mapbox>
  )
}
